import React from "react";
import ErrorBoundary from "lib/ErrorBoundary";
import { SubscriptionsProvider } from "lib/hooks/useSubscriptions/subscriptionsContext";
import SubscriptionsModal from "components/Subscriptions/SubscriptionsModal/SubscriptionsModal";
import { NotFound } from "pages/NotFound/NotFound";
import { LoadingSpinner } from "lib/Spinner";
import useIsMobile from "lib/hooks/useIsMobile";
import { NotAvailable } from "lib/SimpleTable";
import { titleFromPath } from "app/header/parts/HeaderTitle";
import { getPathname } from "lib/routing";
import { SidePanel } from "pages/redesign/viewLayout/sidePanel/collaspingPanel/SidePanel";
import { PillsBar } from "pages/redesign/viewLayout/toolbar/PillsBarRedesign";
import { useCreateSearchRequest } from "pages/redesign/publications/api/searchRequest/useCreateSearchRequest";
import { useJSFetchPublications } from "pages/redesign/publications/api/direct/useJSFetchPublications";
import { sanitizeHtml } from "utils/sanitizeHtml";
import { isSafeUrl } from "utils/urlSafety";
import { useFetchLandingPage } from "./useFetchLandingPage";
import {
  PocHtmlContent,
  LandingPublications,
  PocHashtagPublications,
  LandingPageAdverts
} from "./parts/LandingPageParts";
import styles from "./LandingPage.module.scss";

export function LandingPage({ match }) {
  const [pageData, isLoading, error] = useFetchLandingPage(match.params.topic);
  const searchRequest = useCreateSearchRequest(pageData);
  const searchResults = useJSFetchPublications(searchRequest, !!pageData);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <NotAvailable />;
  if (!pageData) return <NotFound />;
  const hasRedirect =
    pageData?.redirect_link !== undefined &&
    pageData?.redirect_link !== "" &&
    pageData?.redirect_link !== null;

  const canRedirect =
    hasRedirect &&
    window.location.href !== pageData.redirect_link &&
    isSafeUrl(pageData.redirect_link);

  if (canRedirect) {
    window.location.href = pageData.redirect_link;
    return <></>;
  }

  return (
    <SubscriptionsProvider>
      {hasRedirect && !canRedirect && <span data-testid="unsafe-redirect-blocked" style={{ display: "none" }} />}
      <LandingPageView {...pageData} searchResults={searchResults} contentPath="1" />
      <SubscriptionsModal modalTitle="Subscriptions for Nomura Research" />
    </SubscriptionsProvider>
  );
}

function getLandingPageFilters(reportFilter, reportFilterParams, title) {
  let landingPageFilters = reportFilterParams?.[1];

  if (reportFilter?.["customData.featured"]) {
    landingPageFilters = landingPageFilters?.replace("true", title);
  }

  return `?${landingPageFilters}`;
}

function LandingPageView({
  html_content,
  reportFilter,
  hashtags,
  html_file,
  advert_ids,
  template_style,
  title,
  searchResults,
  report_filter_url: reportFilterUrl
}) {
  const isMobile = useIsMobile();
  const aggregations = searchResults[3];
  const reportFilterParams = reportFilterUrl?.split("?");
  const landingPageFilters = getLandingPageFilters(reportFilter, reportFilterParams, title);
  const isLoading = searchResults?.[1];

  return (
    <ErrorBoundary>
      <div className={styles.sectionContainer}>
        {!isMobile && <SidePanel aggregations={aggregations} isLoading={isLoading} style={styles.sidePanel} />}
        <section className={`${styles.section} position-relative px-2`}>
          <h2 className={`${styles.title} mb-3`}>{title || titleFromPath(getPathname())}</h2>
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(html_content) }}
            className={`${styles.htmlContent} ${template_style}`}
          />
          <PillsBar persistentContent={landingPageFilters} />
          {html_file && <PocHtmlContent filename={html_file} />}
          {advert_ids && <LandingPageAdverts advertIds={advert_ids} />}
          {reportFilter && <LandingPublications filter={reportFilter} title={title} searchResults={searchResults} />}
          {hashtags && <PocHashtagPublications hashtags={hashtags} />}
        </section>
      </div>
    </ErrorBoundary>
  );
}
